import { Array as YArray, Doc as YDoc, applyUpdateV2 } from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { Awareness } from 'y-protocols/awareness';
import { C, IAttendee, IImage, ILayerSettings, IMessage, IRoomAsset, IRoomSettings, IToken, L, LayerDelta, R } from './_api';
import { Account } from './account';
import { PointData } from 'pixi.js';
import { Gfx } from 'src/graphics/gfx';
import { Store } from './store';
import { Dice } from 'src/util/dice';
import { Settings } from 'src/graphics/settings';
import { IndexDB } from 'src/util/indexdb';
import { YArrayExtended, YDictionary, YDocument, YEntity, YMapExtended } from 'src/yjs/ydocument';
import { StatelessMessage } from 'src/app/messenger-service';
import { Stroke } from 'src/graphics/graffiti';

/* Server-synchronized */
export class Room {
    public static ownerId: string;
    public static document: YDocument;
    public static chat: YArrayExtended<IMessage>;
    public static attendance: YDictionary<IAttendee>;
    public static settings: YEntity<IRoomSettings>;
    public static layerPolygons: YMapExtended<YArray<LayerDelta>>;
    public static layerLines: YMapExtended<YArray<LayerDelta>>;
    public static tokenAssets: YMapExtended<IRoomAsset>;
    public static imageAssets: YMapExtended<IRoomAsset>;
    public static layers: YDictionary<ILayerSettings>;
    public static tokens: YDictionary<IToken>;
    public static images: YDictionary<IImage>;
    public static lasers: YMapExtended<YArray<PointData>>;
    public static strokes: YMapExtended<YArray<Stroke>>;
    public static awareness: Awareness;
    public static id: string;
    private static provider: HocuspocusProvider;

    public static initialize(provider: HocuspocusProvider, id: string, ownerId: string) {
        Room.document = new YDocument(provider.document);
        Room.chat = Room.document.getArray<IMessage>(R.Chat);
        Room.attendance = Room.document.getDictionary<IAttendee>(R.Attendees);
        Room.settings = Room.document.getEntity<IRoomSettings>(R.Settings, {
            counter: 0,
            refereeIds: [],
            freedom: false
        });
        Room.layerLines = Room.document.getMap<YArray<LayerDelta>>(R.LayerLines);
        Room.layerPolygons = Room.document.getMap<YArray<LayerDelta>>(R.LayerPolygons);
        Room.tokenAssets = Room.document.getMap<IRoomAsset>(R.TokenAssets);
        Room.imageAssets = Room.document.getMap<IRoomAsset>(R.ImageAssets);
        Room.layers = Room.document.getDictionary<ILayerSettings>(R.Layers);
        Room.tokens = Room.document.getDictionary<IToken>(R.Tokens);
        Room.images = Room.document.getDictionary<IImage>(R.Images);
        Room.lasers = Room.document.getMap<YArray<PointData>>(R.Lasers);
        Room.strokes = Room.document.getMap<YArray<Stroke>>(R.Strokes);
        Room.id = id;
        Room.ownerId = ownerId;
        Room.provider = provider;
        Room.initIfNew();
        Room.setupLocalSave();

        /* Register the current user as an attendee */
        const account = Account.settings.get();
        Room.attendance.modify(Account.id, x => {
            x.name = account.name;
            x.color = account.color;
        });

        /* Setup awareness protocol */
        Room.awareness = provider.awareness as Awareness;
        Room.awareness.getStateFields = function (field: string): any[] {
            const output = [];
            for (let state of this.getStates()) {
                const actual = state[1];
                output.push(actual[field]);
            }
            return output;
        }

        Store.initialize();
    }

    public static setupLocalSave() {
        let lastLength = 0;
        const saveLocal = async () => {
            const state = Room.document.getStateAsUpdate();
            /* Comparing the length is naive, but good enough for our use case. */
            if (lastLength != state.length) {
                await IndexDB.saveArrayBuffer(this.id, state);
                lastLength = state.length;
            }
        };
        setInterval(() => saveLocal(), 20000);
    }

    // TODO: Safeguard this destructive operation, also move this into Token
    public static deleteToken(assetId: string): boolean {
        const asset = Room.tokenAssets.get(assetId)
        if (asset == undefined) {
            return false;
        }

        if (Store.isReferee.value == false && Account.id != asset.uploaderId) {
            return false; // We are not a referee and not the uploader
        }

        const tokens = Room.tokens.toJSON();
        const tokensToDelete: string[] = [];
        for (let id in tokens) {
            if (tokens[id].assetId == assetId) {
                tokensToDelete.push(id);
            }
        }

        for (let id of tokensToDelete) {
            Room.tokens.delete(id);
        }
        Room.tokenAssets.delete(assetId);

        return true;
    }

    public static async addImage(fileId: string, x: number, y: number) {
        const asset: IRoomAsset = {
            id: crypto.randomUUID(),
            name: fileId,
            uploaderId: Account.id
        }

        const image: IImage = {
            id: asset.id,
            position: { x, y },
            rotation: 0,
            scale: { x: 1, y: 1 },
            gridScale: 100,
            visible: true,
            locked: false,
        };

        Room.document.transact(() => {
            Room.imageAssets.set(asset.id, asset);
            Room.images.set(asset.id, image);
        });
    }

    public static loadScene(data: Uint8Array) {
        const savedDocument = new YDoc();
        applyUpdateV2(savedDocument, data);
        const sourceDocument = new YDocument(savedDocument);
        const maps: string[] = [R.TokenAssets, R.ImageAssets];
        const dictionaries: string[] = [R.Tokens, R.Images, R.Layers];

        // TODO: Don't clear data, set the data one by one if and only if it is different
        // TODO: Load the data in order _not_ as a transaction so the UI can pick up changes in the correct order
        Room.document.transact(() => {
            for (let name of maps) {
                const source = sourceDocument.getMap(name);
                const destination = Room.document.getMap(name);
                destination.clear();

                for (let key of source.keys()) {
                    const value = source.get(key);
                    destination.set(key, value);
                }
            }

            for (let name of dictionaries) {
                const source = sourceDocument.getDictionary(name);
                const destination = Room.document.getDictionary(name);
                destination.clear();

                for (let key in source.toJSON()) {
                    const value = source.get(key);
                    destination.set(key, value);
                }
            }

            Room.layerPolygons.forEach((layer, key) => {
                const savedLayers = sourceDocument.getMap<YArray<LayerDelta>>(R.LayerPolygons);
                const destination = savedLayers.get(key) as YArray<LayerDelta>;
                layer.delete(0, layer.length);
                layer.insert(0, destination.toArray());
            });

            /* Remove invalid layers */
            const layerMap = Room.document.getMap(R.Layers);
            const layers = Object.keys(layerMap.toJSON());
            for (let layer of layers) {
                if (Gfx.layers[layer] == null) {
                    layerMap.delete(layer);
                }
            }
            savedDocument.destroy();
        });
    }

    public static shout(message: string, channel = C.All, receiverId = '', senderId = Account.id) {

        let rolls = channel !== C.AI ? Dice.roll(message) : undefined;

        if (rolls) {
            Store.playDicerollSFX();
        } else {
            message = Dice.interpolate(message);
        };

        Room.chat.insert(0, [{
            accountId: senderId,
            date: Date.now(),
            rolls: rolls,
            text: message,
            channel: channel,
            receiverId: receiverId
        }]);
    }

    public static initFog(fogcolor = '0x333333') {
        /* Fog setup */
        let fog = Room.layerPolygons.get(L.Fog) ?? Room.layerPolygons.set(L.Fog, new YArray<LayerDelta>());
        const path = [
            0, 0,
            0, Settings.worldSize,
            Settings.worldSize, Settings.worldSize,
            Settings.worldSize, 0,
            0, 0
        ];
        fog.insert(0, [{ d: true, p: path }]);
        Room.layers.modify(L.Fog, x => x.fillColor = fogcolor);
    }

    private static initIfNew() {
        const isNewRoom = Room.attendance.length() == 0;
        if (isNewRoom == false) return;

        console.info('New room setup. Setting the room owner as the referee.');
        Room.attendance.modify(Room.ownerId, x => x.referee = true)

        Room.initFog();
    }

    public static sendStateless(message: StatelessMessage) {
        this.provider.sendStateless(message.json());
    }

    public static getAttendee(name: string) {
        const target = name.toLowerCase();
        const found = Object.entries(Room.attendance.toJSON()).find(([id, data]) => data.name.toLowerCase() === target);
        if (found) {
            return { id: found[0], user: found[1] }; // accountId
        } else {
            return null;
        }
    }
}
